/**
 * I contratti che descrivono che forma hanno le risposte e le richieste comuni.
 *
 * ## Qui c'era anche una busta che non esiste
 *
 * `ApiResponse<T> = { data: T; message?: string }` stava in cima a questo file come «la
 * busta comune dell'API», e nessuna rotta l'ha mai usata: il server restituisce l'oggetto
 * nudo, e per gli elenchi la busta è `Paginated<T>`, che ha `meta` e non `message`. Non
 * era nominata da nessuno in tutto il repo — con l'aggravante che `@nestjs/swagger` esporta
 * un `ApiResponse` suo, che il server importa davvero, così le due volte che quella parola
 * compare in `apps/api` sono il decoratore e non questa.
 *
 * Un tipo che descrive male il filo è peggio di un tipo che manca: chi scriverà il terzo
 * client lo leggerà per sapere che cosa aspettarsi, e scarterà `data` da una risposta che
 * non ce l'ha. Tolto.
 */
import { ParticipantStatus, ShiftKind } from './enums.js';
import { TELEFONO_MAX_SCRITTO } from './anagrafica.js';
/**
 * Le cinque caselle qui sopra, fatte passare dal contratto invece che riscritte a mano.
 *
 * Non trasforma niente: ricopia quel che riceve. Serve a mettere un tipo dove
 * altrimenti non ce n'è nessuno, e a nient'altro — chi la trova e la scambia per un
 * giro a vuoto tolga prima questo commento, così almeno sa che cosa sta togliendo.
 *
 * ## Perché copia invece di restituire quel che le è arrivato
 *
 * Perché `PaginationQuery` è un'**interfaccia**, e un'interfaccia senza indice non è
 * assegnabile a un `Record<string, ...>`: solo i tipi anonimi ricevono l'indice
 * implicito. Lo sparpagliamento (`{ ...caselle }`) produce un tipo anonimo, e quello
 * passa. Togliere la copia non semplifica: non compila.
 *
 * ## Perché serve
 *
 * I due client mandano i parametri di ricerca dentro un `Query`, che da entrambe le
 * parti è `Record<string, ...>`: accetta **qualunque** nome. Va bene per i filtri, che
 * sono di ogni rotta i suoi e cambiano da una all'altra; non va bene per `page`,
 * `perPage`, `search`, `sortBy`, `sortDir`, che sono gli stessi dappertutto e in questo
 * repo erano riscritti a mano in quarantasei punti — ventinove nella app, diciassette nel
 * pannello. Lì un refuso non è un errore per nessuno: `perPag` parte, e il server — che
 * monta `ValidationPipe` con `forbidNonWhitelisted` — non lo ignora, risponde **400
 * all'intera richiesta**. Non si perde l'ordinamento: si perde l'elenco.
 *
 * Tre di quei punti nessuna misura automatica li vedeva, perché non erano scritti sotto
 * una chiave `query:`: un compositore che restituisce `Query` (`comune()` in
 * `SchermataSpedizioni.tsx`), il ciclo d'esportazione di `useElenco.ts`, e — il peggiore
 * — un indirizzo con le caselle dentro la stringa
 * (`'/users?…&perPage=200&sortBy=…'`, in `DipendenteView.vue`). Là dentro non erano
 * nemmeno del tipo sbagliato: erano testo, e il testo non lo controlla nessuno.
 *
 * Un `Query & PaginationQuery` non basterebbe: l'indice di `Query` disarma il controllo
 * delle proprietà in eccesso, quindi prenderebbe i tipi sbagliati e lascerebbe passare
 * proprio i refusi. Passando di qui, invece, l'oggetto scritto fra le parentesi è un
 * letterale e il compilatore gli conta le caselle una per una: il nome sbagliato è rosso
 * mentre lo si scrive, e un `sortDir` che non sia `'asc'` o `'desc'` non passa. Il
 * giorno che il contratto ne rinomina una cadono insieme la app, il pannello e il server
 * (`PaginationQueryDto implements PaginationQuery`).
 *
 * Si usa spargendola dentro il resto della ricerca, che resta libera:
 *
 * ```ts
 * query: { mine: true, status, ...paginazione({ page: 1, perPage: PER_PAGINA }) }
 * ```
 */
export function paginazione(caselle) {
    return { ...caselle };
}
/**
 * Il numero massimo di righe che una singola pagina può chiedere.
 *
 * Sta qui, e non in un `@Max` sul server e in un numero scritto a mano nel
 * client, perché i due non erano d'accordo: il client ne chiedeva 500, il
 * server ne ammetteva 200, e una richiesta oltre il limite non viene troncata —
 * viene **rifiutata**. Il risultato non era un'esportazione più corta, era
 * nessuna esportazione, su ogni elenco, sempre. Un limite conosciuto da una
 * parte sola non è un limite condiviso: è due limiti che prima o poi divergono.
 *
 * Un elenco non lo segue, e apposta: `AVVISI_PER_PAGINA_MAX`, qui sotto, si ferma
 * a metà perché consegna il corpo intero di ogni avviso. Sta scritto in tutti e
 * due i punti così che la differenza si veda da qualunque parte la si guardi.
 */
export const PER_PAGE_MAX = 200;
/**
 * Quanto può essere lunga la stringa scritta nella casella di ricerca di un elenco.
 *
 * Il numero era in `PaginationQueryDto`, con accanto la ragione per cui esiste:
 * quella stringa arriva al database come argomento di un `ILIKE`, e l'indice a
 * trigrammi smette di aiutare molto prima di centoventi caratteri. Nessuno cerca
 * un elenco con un paragrafo; quello che è lungo così o è un incollaggio andato
 * male o è qualcuno che sta tastando.
 *
 * Sta qui perché le **diciotto** caselle di ricerca del pannello non lo
 * conoscevano. Quindici di quelle diciotto passano da `useElenco`, che manda
 * esattamente `search`: incollare in una di esse una stringa più lunga non dava
 * meno risultati, dava un 400 — e con `forbidNonWhitelisted` addosso, un 400
 * sull'intera richiesta, mentre a schermo c'è solo una casella di ricerca che ha
 * smesso di funzionare. Le altre tre filtrano in casa e il tetto non le tocca; lo
 * portano lo stesso, perché la frase sopra vale anche per loro.
 */
export const RICERCA_MAX = 120;
/**
 * Quanto dev'essere lungo un «perché», per ciascuna delle tre misure che questo
 * repo usa davvero.
 *
 * Stessa storia di `PER_PAGE_MAX` qui sopra, ma scritta in molte più copie: il
 * server dichiara `reason` in **ventitré** classi DTO con `@MinLength` e
 * `@MaxLength`, e i due client si riscrivevano quei numeri a mano — sette
 * `MOTIVO_MINIMO` nella app, quindici soglie nel pannello, più i tetti dei campi
 * di testo. Quarantasette copie di un fatto solo. Oggi combaciavano tutte: è la
 * forma di §60 — una misura ricopiata non sbaglia, invecchia — e il giorno che il
 * server alza un minimo il bottone si accende lo stesso e la risposta è un 400
 * sull'intera operazione. Non si perde il testo del motivo: si perde
 * l'annullamento.
 *
 * ## Perché tre, e perché portano il nome dei numeri e non di una ragione
 *
 * Il primo tentativo di scrivere questo blocco raggruppava per intenzione —
 * «disfare un'operazione» chiede tre caratteri, «negare a una persona quel che ha
 * chiesto» ne chiede cinque — e i dati l'hanno smentito prima che il commento
 * finisse. I rifiuti sul server sono tre: `RejectExpenseNoteDto` chiede cinque
 * («a un rifiuto senza motivo non si può rispondere», dice il suo messaggio),
 * `RejectJustificationDto` cinque, e `RejectUniformRequestDto` **tre**. Stessa
 * cosa, due soglie. E `RevokePaycheckDto` — togliere a una persona la busta paga
 * che aveva già — di caratteri ne chiede tre, come dismettere un magazzino.
 *
 * Quindi i gruppi qui sotto dicono i numeri e basta, che è l'unica cosa vera:
 *
 * - `breve` 3–500 — diciotto classi su ventitré.
 * - `motivato` 5–500 — due: il rifiuto di una nota spese, l'annullo di una trasferta.
 * - `ampio` 5–1000 — i giustificativi, che si sono presi più spazio.
 *
 * Restano fuori due classi che un minimo non ce l'hanno (`CancelJustificationDto`,
 * `RevokeFreeBusyDto`): usano solo `.max`.
 *
 * Il disaccordo fra i tre rifiuti **non** è appianato qui. Allinearlo vorrebbe
 * dire cambiare che cosa il server accetta, e questa voce lega numeri, non li
 * decide: legandoli, però, adesso si vedono tutti insieme, e chi vorrà allinearli
 * troverà un posto solo dove farlo.
 *
 * ## La trappola che questa costante non copre, e va detta
 *
 * Nell'interfaccia italiana la parola «motivo» sta su **due** campi diversi del
 * server: questo `reason`, e il `purpose` della trasferta — il perché la
 * trasferta esiste, che di caratteri ne ammette mille. Applicare `operativo.max`
 * a una casella che manda `purpose` non è un tetto sbagliato di poco: taglia a
 * metà quello che le persone possono scrivere. Le due caselle si chiamano uguale
 * a schermo e sono cose diverse; prima di legarne una, si guarda quale delle due
 * chiavi parte.
 */
export const MOTIVO = {
    /** 3–500: la misura ordinaria, diciotto classi DTO su ventitré. */
    breve: { min: 3, max: 500 },
    /** 5–500: nota spese rifiutata, trasferta annullata. */
    motivato: { min: 5, max: 500 },
    /** 5–1000: i giustificativi. */
    ampio: { min: 5, max: 1000 },
    /** 1–200: la chiusura di un reparto, una riga che legge chi pianifica (APS-MES, [[K725]]). */
    chiusura: { min: 1, max: 200 },
};
/**
 * L'altro «motivo»: il `purpose` della trasferta — perché la trasferta esiste.
 *
 * Sta qui accanto a `MOTIVO` e non dentro, perché non è un suo gruppo: è la
 * trappola che il commento di `MOTIVO` descrive, messa dove chi la incontra
 * possa legarla invece di indovinarla. A schermo le due caselle si chiamano
 * uguale — «Motivo» — e mandano chiavi diverse: `reason`, che al massimo arriva
 * a cinquecento caratteri, e questo `purpose`, che arriva a mille. Chi mette un
 * tetto senza guardare quale delle due parte non sbaglia di poco: dimezza quello
 * che le persone possono scrivere, e lo fa in silenzio, perché un `maxlength`
 * troppo stretto non dà errore — smette di accettare i tasti.
 *
 * Quattro copie oggi: `CreateBusinessTripDto.purpose`, `UpdateBusinessTripDto.purpose`
 * (che il numero lo dice anche a parole nel proprio messaggio), il campo del
 * pannello in `TrasferteView.vue` e il `MOTIVO_MASSIMO` della app in
 * `CorrezioneTrasferta.tsx` — che di nome è «motivo» ma di campo è questo.
 */
export const SCOPO_TRASFERTA_MAX = 1000;
/**
 * L'indirizzo di posta, che sul server non aveva un tetto e in un posto solo sì.
 *
 * `email` ed `emailOffice` della persona non avevano **nessun** `@MaxLength`: si
 * appoggiavano a `@IsEmail`, che controlla la forma e non la lunghezza. La
 * colonna è `text`, quindi un indirizzo da quattromila caratteri entrava e
 * restava. Nella rubrica invece un tetto c'era, 200, e non era ancorato a niente
 * — lo stesso dato con due politiche, di cui una era «nessuna».
 *
 * 254 non è inventato: l'RFC 5321 dà 256 caratteri al percorso completo comprese
 * le due parentesi angolari, cioè 254 all'indirizzo. È l'unico numero che si
 * possa scrivere qui senza deciderlo noi, ed è più largo del 200 della rubrica:
 * nessuna riga che oggi entra viene chiusa fuori.
 */
export const EMAIL_MAX = 254;
/**
 * Il numero di telefono, che adesso sta in `anagrafica.ts` insieme alla sua forma.
 *
 * Qui c'era `TELEFONO_MAX = 40` con questa giustificazione: «la colonna del vecchio
 * conteneva anche "int. 214" e "chiamare dopo le 18", e quaranta caratteri sono la
 * misura di quel genere di cosa, non di un numero». Contate le righe vere — 1296
 * valori nelle cinque colonne di telefono del vecchio database — quelle che
 * contengono anche una sola lettera sono **zero**, e la più lunga misura dodici
 * caratteri. Il quaranta non veniva da una misura, e un tetto che nessun dato può
 * toccare non lo controlla nessuno (§60).
 *
 * Accanto c'era anche scritto che la rubrica fa diversamente **apposta**, perché là
 * il numero serve a comporre una chiamata. Era vero al contrario: la forma che la
 * rubrica imponeva, `^\+?[1-9]\d{6,17}$`, vieta lo zero iniziale e quindi rifiuta
 * ogni fisso italiano scritto in forma nazionale. Le due costanti se ne sono andate
 * insieme perché erano lo stesso difetto guardato dai due lati — vedi `anagrafica.ts`.
 *
 * Le quattro misure di telefono che restano qui sotto — i due della persona, quello
 * della sede, quello del referente di un luogo — prendono adesso
 * `TELEFONO_MAX_SCRITTO`, non `TELEFONO_MAX`, e la differenza fra i due nomi è la
 * ragione per cui sono due. Queste quattro misure servono a un `maxlength` di una
 * casella, cioè a fermare le dita di una persona **mentre** scrive un numero
 * spaziato; il tetto conservato lo guarda il server sul numero già ripulito, e lo
 * dice la forma. Un solo numero per i due mestieri taglierebbe un prefisso
 * internazionale battuto a gruppi di tre.
 */
/**
 * La via e il comune, che sono la stessa cosa per una sede e per un luogo.
 *
 * Stavano scritti quattro volte per due, con gli stessi due numeri, e stavolta
 * non è il caso di `PROVINCIA` e `NAZIONE`: là due vocabolari diversi dicono per
 * conto proprio «due lettere» e possono smettere di dirlo insieme. Qui è un
 * indirizzo postale italiano tutte e due le volte. Non c'è un futuro in cui la
 * via di un capannone di un fornitore possa essere più lunga della via di una
 * nostra sede.
 */
export const INDIRIZZO = {
    street: { max: 200 },
    city: { max: 120 },
};
/**
 * Le misure della scheda di una persona: nome, matricola, badge, i due telefoni.
 *
 * Il database non ne impone nessuna — sono tutte colonne `TEXT`. Le misure
 * esistono solo nei DTO, e lì stanno scritte **tre volte ciascuna**: nella
 * classe che una persona la crea, in quella che la corregge, e in quella del
 * primo amministratore, che la stessa scheda la compila al primo avvio.
 * Ventuno numeri per sette fatti.
 *
 * Oggi i ventuno sono d'accordo. È esattamente la condizione di §60: una misura
 * ricopiata non sbaglia, invecchia — e finché sono tutti d'accordo non c'è
 * niente da vedere, che è il motivo per cui nessuno guarda. Il giorno che una
 * delle tre classi si muove di un numero, le altre due restano dov'erano e
 * nessuno se ne accorge finché una persona non riesce a salvare.
 *
 * ## Che cosa il pannello ne sapeva: niente
 *
 * Nove delle caselle di `DipendenteView.vue` — l'identificativo, il nome per
 * esteso, nome e cognome, la posta, la matricola, il badge, i due telefoni —
 * non avevano **nessun** tetto. Con `whitelist` e `forbidNonWhitelisted` sul
 * server, una parola di troppo in una qualunque di quelle nove non tronca il
 * campo: rifiuta l'intera anagrafica con un 400, e a schermo non c'è niente che
 * dica quale delle nove. Chi compila una scheda lunga la perde tutta.
 *
 * ## La matricola, che era due numeri e non uno
 *
 * `matricola` è l'unico dei sette che *già* divergeva: quaranta nelle tre
 * classi che la scrivono, e **trentadue** in `PublishPaycheckDto` — che non è
 * un filtro di comodo, è la classe con cui si *trova la persona* a cui
 * depositare il cedolino (`where: { matricola }`).
 *
 * I due numeri non si limitavano a divergere: il più corto chiudeva fuori un
 * caso che il più lungo ammette. Una persona con la matricola da trentatré a
 * quaranta caratteri si crea dal pannello senza un lamento, e il suo cedolino
 * non si può più depositare per matricola — la richiesta viene rifiutata prima
 * ancora che il server vada a cercarla, e il messaggio parla di lunghezza, non
 * di quella persona. Chi carica i cedolini da un programma vedrebbe fallire
 * sempre le stesse poche righe, sempre allo stesso modo, senza niente in vista
 * che spieghi perché proprio quelle. Quaranta è il vero per peso e per
 * conseguenza: allargare la ricerca non apre niente che la scrittura non abbia
 * già aperto.
 *
 * ## La posta, che qui non c'era e adesso c'è
 *
 * Ci stava scritto che `email` ed `emailOffice` restavano fuori perché un tetto
 * sul server non ce l'avevano affatto, e che metterne uno qui sarebbe stato
 * inventarlo. Il ragionamento reggeva su una cosa che non avevo guardato: la
 * rubrica un tetto ce l'ha, `@MaxLength(200)`, e quindi il sistema *aveva già*
 * due politiche per lo stesso dato — una da duecento e una da nessuna. E un
 * numero vero esiste, l'RFC lo scrive: vedi `EMAIL_MAX`. Quello che non si può
 * inventare è un numero **nostro**; ricopiare quello dello standard è il
 * contrario di inventarlo.
 */
export const PERSONA = {
    /** 2–80: l'identificativo con cui si accede. */
    identifier: { min: 2, max: 80 },
    /** 2–120: il nome per esteso, quello che le altre schermate stampano. */
    displayName: { min: 2, max: 120 },
    firstname: { max: 80 },
    lastname: { max: 80 },
    /** 40, non 32. Vedi sopra: era il numero con cui un cedolino non si depositava. */
    matricola: { max: 40 },
    badge: { max: 40 },
    phoneNumber: { max: TELEFONO_MAX_SCRITTO },
    phoneOffice: { max: TELEFONO_MAX_SCRITTO },
    /** Vedi `EMAIL_MAX`: prima non c'era, e non c'era nemmeno nei DTO. */
    email: { max: EMAIL_MAX },
    emailOffice: { max: EMAIL_MAX },
};
/**
 * La forma di un **codice a macchina**, costruita dalla misura invece che
 * ricopiata dentro di essa.
 *
 * `^[a-z0-9_]{min,max}$` sta scritto in quattro punti: il tipo di collo, il tipo
 * di nota spese, il capo di vestiario e la casella del pannello dove un capo si
 * crea. Tre di quei quattro erano `{2,30}` battuti a mano.
 *
 * I tre numeri non li ho fusi in una costante sola, ed è la stessa ragione di
 * `PROVINCIA` e `NAZIONE`: sono tre cataloghi diversi che per conto proprio
 * dicono ciascuno «da due a trenta», e il giorno che uno dei tre allarga il
 * proprio codice non deve trascinarsi dietro gli altri due. **Uguale non vuol
 * dire legato.** Quello che si toglie di mezzo è la ripetizione vera — la
 * *classe di caratteri* — così che il giorno in cui un codice dovrà ammettere il
 * trattino la riga da cambiare sia una, e chi la cambia veda subito chi ne
 * dipende.
 *
 * In che cosa non somiglia a `codiceParlante`: qui la prima posizione può essere
 * una cifra o un trattino basso, quindi `_1` e `9x` sono codici validi.
 */
export function codiceMacchina(min, max) {
    return new RegExp(`^[a-z0-9_]{${min},${max}}$`);
}
/**
 * L'altra forma: come `codiceMacchina`, ma la prima posizione dev'essere una
 * lettera — il codice si legge, non si conta.
 *
 * È la forma di `CODICE_RUOLO`, e anche quella del codice del tipo di
 * giustificativo, che però se la scriveva per conto suo come `^[a-z][a-z0-9_]*$`
 * — cioè **senza nessuna misura dentro** — e i due estremi li affidava a un
 * `@MinLength(2)` e a un `@MaxLength(50)` due righe più sotto. Due controlli
 * sulla stessa cosa in due punti diversi sono il modo in cui una misura si
 * allontana dalla propria forma senza che nessuno se ne accorga: allargare il
 * `@MaxLength` non allargava niente se la forma non ammetteva quel carattere, e
 * stringerlo lasciava la forma a dire il contrario.
 */
export function codiceParlante(min, max) {
    return new RegExp(`^[a-z][a-z0-9_]{${min - 1},${max - 1}}$`);
}
/**
 * Il codice di un ruolo: la misura, e la forma **derivata** dalla misura.
 *
 * La forma era una costante privata di `role.dto.ts` — la definizione vera di
 * che cosa un codice di ruolo è: minuscole, cifre e trattini bassi, prima
 * lettera obbligatoria, da due a quaranta caratteri. Ma i due limiti che quella
 * forma già contiene erano riscritti a mano altre quattro volte, con
 * `@MinLength(2)` e `@MaxLength(40)`, nelle classi che il codice lo ricevono
 * senza controllarne la forma (`CreateUserDto`, `UpdateUserDto`,
 * `ListUsersQueryDto`) — e una quinta volta **sbagliata**: la scelta del
 * pubblico di una notifica ne ammetteva sessanta.
 *
 * Il sessanta non fa danno nella direzione in cui sbaglia, e per questo è il
 * modo peggiore di sbagliare, non il più innocuo: un codice di quarantuno
 * caratteri non viene rifiutato, semplicemente non corrisponde a nessun ruolo.
 * Il pubblico esce vuoto, la notifica non raggiunge nessuno, e chi ha battuto
 * male il codice non riceve un errore — riceve silenzio, ed è convinto di aver
 * mandato.
 *
 * `CODICE_RUOLO` si **costruisce** da `CODICE_RUOLO_MIN` e `CODICE_RUOLO_MAX`
 * invece di scrivere quei due numeri dentro la forma e un'altra volta accanto:
 * due misure che vivono in una riga sola non possono allontanarsi. E sta in
 * `shared` e non nel server perché la casella del pannello dove il codice si
 * scrive non poteva vederla — era privata di un file di `apps/api`, quindi il
 * pannello lasciava battere qualunque cosa e il rifiuto arrivava dopo, a
 * modulo già compilato.
 */
export const CODICE_RUOLO_MIN = 2;
/** Quaranta come `PERSONA.matricola`, ma per un'altra ragione: è una coincidenza, non un legame. */
export const CODICE_RUOLO_MAX = 40;
export const CODICE_RUOLO = codiceParlante(CODICE_RUOLO_MIN, CODICE_RUOLO_MAX);
/**
 * Il tetto di una password, che non è una preferenza: è un fatto di argon2.
 *
 * Senza, un client può far macinare diciannove mebibyte di memoria per ogni
 * megabyte spedito, e il costo lo paga il server per intero.
 *
 * Il numero c'era già, ma solo su **due** delle quattro classi che una password
 * la ricevono — `ChangePasswordDto` e `PrimoAmministratoreDto`, cioè le due dove
 * una persona sceglie la password *per sé*. Mancava dove qualcuno la sceglie
 * **per un altro**: la creazione di una scheda e l'azzeramento amministrativo.
 * Sono esattamente le due raggiungibili con un token valido in mano, cioè quelle
 * per cui il tetto era stato messo. La ragione scritta nelle prime due vale
 * parola per parola anche per le altre due, e lì non c'era.
 *
 * Il **minimo** invece non sta qui e non ci può stare: è
 * `security.minPasswordLength`, che si legge dalla configurazione, mentre un
 * decoratore è valutato al caricamento della classe. Vive in `AuthService`.
 */
export const PASSWORD_MAX = 200;
/**
 * Il tetto sul **codice di ripristino** scritto a mano, prima ancora di guardarlo.
 *
 * Il codice normalizzato è di venti simboli e la forma che si consegna ne ha ventitré
 * (`apps/api/src/auth/codice-ripristino.ts`, dove sta tutto il ragionamento). Questo numero
 * lascia margine a chi lo ribatte spaziato o con qualche trattino in più, e chiude fuori il
 * corpo di richiesta da un megabyte — la stessa igiene che `PASSWORD_MAX` applica all'altro
 * campo dello stesso identico modulo.
 *
 * Sta qui e non accanto alle tre funzioni che il codice lo generano, perché lo stesso modulo
 * lo disegna anche la app: la casella dove il codice si batte deve avere il **medesimo**
 * tetto della classe che lo riceve, e finché il numero era privato di un file di `apps/api`
 * l'unico modo per dargliela era ricopiarlo. È §60 alla lettera — una misura ricopiata non
 * sbaglia, invecchia — e la lezione l'ha già insegnata `PASSWORD_MAX`, che il pannello non
 * poteva vedere.
 */
export const CODICE_SCRITTO_MAX = 64;
/**
 * Quanto si aspetta fra due richieste del codice di ripristino.
 *
 * ## Il fatto che l'ha fatto nascere, misurato
 *
 * La notte del passaggio in produzione — 12 ottobre 2026 — diciassette persone hanno chiesto il
 * codice in dieci ore, e **otto l'hanno chiesto più di una volta**: una cinque volte in sei
 * secondi, una cinque volte in quattro secondi, una cinque volte nell'arco di sette ore e mezzo.
 * Quattro non sono rientrate affatto.
 *
 * La causa non era la posta — il relay ha preso in carico tutte le buste, e tredici persone su
 * diciassette il codice l'hanno riscattato — era il **pulsante che non diceva niente**: premendo
 * «Password dimenticata» cambiava una riga di didascalia in fondo alla schermata, e chi aveva il
 * telefono in mano premeva di nuovo.
 *
 * ## Perché l'attesa, e non solo un pulsante più chiaro
 *
 * Perché ogni richiesta **spegne il codice di prima**: il server tiene un codice pendente per
 * persona e l'ultimo vince (`AuthService.creaCodiceRipristino`). Chi preme cinque volte riceve
 * cinque email e **quattro portano un codice morto**; se apre la prima — che in molti lettori di
 * posta è anche quella in cima — si vede rifiutare proprio perché aveva insistito. L'attesa
 * trasforma un doppio tocco in niente, invece che in un codice bruciato.
 *
 * ## Perché sessanta, e perché qui
 *
 * Sessanta è il tempo sotto cui una persona non sta ancora aspettando una email: sta premendo di
 * nuovo. Più in alto diventerebbe un castigo per chi la email **non** l'ha ricevuta davvero, che
 * è il caso in cui si ha più fretta.
 *
 * Sta in `shared` per la ragione di `CODICE_SCRITTO_MAX`: lo guardano **tutt'e due le metà**, e
 * devono dire lo stesso numero. Il server rifiuta in silenzio una richiesta arrivata troppo
 * presto; la app conta alla rovescia il comando per chiederlo di nuovo. Un conto alla rovescia
 * più corto del rifiuto è peggio di nessun conto alla rovescia: arriva a zero, la persona preme,
 * e non succede niente — cioè si ricasca nel sintomo di partenza con un orologio davanti.
 */
export const RESPIRO_CODICE_SECONDI = 60;
/**
 * Le tre classi che una password deve contenere: la **forma** e le parole con
 * cui chiederla, nello stesso posto.
 *
 * La forma era scritta a mano quattro volte sul server, sempre uguale, e i due
 * client ne tenevano una parafrasi: `apps/web/src/lib/password.ts` e
 * `apps/mobile/src/schermate/accesso/password.ts` disegnano le spunte verdi che
 * dicono a chi scrive *quale* requisito manca, e le loro prove erano tre
 * espressioni separate che «devono essere la stessa regola» del server. Il
 * commento lo chiedeva a parole. Nessuno lo verificava.
 *
 * Non è un timore per iscritto, è già successo due volte, ed è scritto in
 * `password.ts`: una prova ammetteva un simbolo dove il server pretende una
 * cifra, e un'altra contava le accentate come maiuscole dove `[A-Z]` non le
 * contiene. Le due volte il sintomo è stato lo stesso — l'elenco tutto verde, il
 * pulsante acceso, e il 400 dopo la pressione. È il guasto peggiore che una
 * spunta anticipatrice possa produrre: promette e poi smentisce.
 *
 * Qui la classe si scrive **una volta** e serve a tre cose che non possono più
 * discordare: `PASSWORD_COMPLESSITA` si costruisce dalle tre `forma`, le tre
 * `richiesta` sono le frasi che il pannello stampa accanto alla spunta, e le tre
 * `chiave` sono le righe con cui la app chiede la stessa cosa nelle sue tre
 * lingue. La `chiave` è un letterale: la app la passa al suo dizionario, che è
 * un'unione di chiavi, quindi il giorno in cui una riga sparisse dalle
 * traduzioni non ci sarebbe una spunta muta — non compilerebbe.
 *
 * `[0-9]` e non `\d`: sono la stessa cosa in JavaScript senza il flag `u`, ma la
 * prima è quella che la frase dice a chi legge, e una forma che si legge come la
 * sua frase è una forma che nessuno riscrive per sbaglio in un altro modo.
 *
 * L'ordine è quello che le spunte hanno sempre avuto a schermo — maiuscola,
 * minuscola, cifra — e non quello in cui il server aveva scritto la sua
 * espressione. Tre `(?=…)` sono asserzioni di larghezza nulla valutate tutte
 * nella stessa posizione: scambiarle non cambia che cosa la regola accetta,
 * mentre scambiare le righe di un elenco lo vedrebbe chi lo legge.
 */
export const PASSWORD_CLASSI = [
    {
        forma: /[A-Z]/,
        richiesta: 'Una lettera maiuscola, dalla A alla Z',
        chiave: 'password.maiuscola',
    },
    {
        forma: /[a-z]/,
        richiesta: 'Una lettera minuscola, dalla a alla z',
        chiave: 'password.minuscola',
    },
    { forma: /[0-9]/, richiesta: 'Una cifra, da 0 a 9', chiave: 'password.cifra' },
];
/** Le tre classi in un colpo solo, per il `@Matches` del server. */
export const PASSWORD_COMPLESSITA = new RegExp(PASSWORD_CLASSI.map(({ forma }) => `(?=.*${forma.source})`).join(''));
/**
 * Il minimo di una password **provvisoria**, quella che qualcuno compone per un
 * altro: creazione di una scheda e azzeramento amministrativo.
 *
 * Non è il minimo generale. Quello è `security.minPasswordLength` (di serie 12),
 * si legge dalla configurazione e vive in `AuthService`, perché un decoratore è
 * valutato al caricamento della classe e la configurazione allora non c'è.
 *
 * Il numero qui è **dieci**, ed è più basso di proposito. La domanda è stata
 * posta a Cosimo — alzarlo a dodici e togliere la differenza, o tenerlo — e la
 * risposta è **resta dieci**. Quel che segue è la ragione, perché un numero
 * senza la sua ragione al giro dopo sembra una svista:
 *
 * una password provvisoria **non si sceglie, si detta**. Chi crea la scheda la
 * compone per un altro e gliela dice — a voce in corridoio, al telefono, su un
 * biglietto — e quell'altro la batte una volta sola, perché al primo ingresso è
 * obbligato a sostituirla. Vive minuti. I due caratteri in meno costano poco
 * contro un attacco che deve indovinare in quei minuti una stringa con
 * minuscola, maiuscola e cifra, e valgono molto contro l'errore che càpita
 * davvero: una password lunga dettata male, ribattuta male, e una persona che
 * chiama l'ufficio perché non entra. La sicurezza vera di questa credenziale non
 * sta nella sua lunghezza, sta nel fatto che **scade all'uso**.
 *
 * Il minimo generale, quello che la persona sceglie per sé e che le resta
 * addosso per mesi, è un'altra cosa e non si tocca: dodici di serie, e alzabile
 * dalla configurazione senza ricompilare niente. Se un giorno lo si alza, questo
 * numero **non lo segue**, ed è voluto: sono due politiche, non due copie.
 *
 * Sta qui perché era scritto a mano quattro volte in due file (decoratore,
 * `minLength` di Swagger e la frase della descrizione, per due classi): così il
 * giorno in cui la politica cambia è una riga sola, e nel frattempo le quattro
 * non possono discordare fra loro.
 */
export const PASSWORD_PROVVISORIA_MIN = 10;
/** Le posizioni in cui l'omocodia sostituisce una cifra con una lettera. */
const OMOCODIA = /[0-9LMNPQRSTUV]/;
/**
 * Il codice fiscale italiano, in pezzi — perché i sedici caratteri siano
 * **contati**, non affermati.
 *
 * La forma era una costante di `create-user.dto.ts`, condivisa fra le tre classi
 * del server ma invisibile al pannello: la scheda del dipendente scriveva
 * `maxlength="16"` a mano, e il modulo di primo avvio non scriveva niente. Due
 * caselle per lo stesso dato, con due tetti diversi, e nessuno dei due legato
 * alla forma che li giustifica entrambi.
 *
 * Scomporla in pezzi non è un vezzo: `CODICE_FISCALE_LUNGHEZZA` è la **somma**
 * di quei pezzi, non un sedici scritto accanto. Il numero nella forma e il
 * numero nella casella sono ora lo stesso numero.
 *
 * Le **omocodie** sono la ragione per cui certe posizioni ammettono lettere:
 * quando due persone generano lo stesso codice, l'Agenzia delle Entrate ne
 * sostituisce le cifre secondo una tabella fissa (`0→L`, `1→M`, … `9→V`). Una
 * forma che pretende cifre in quelle posizioni rifiuta codici perfettamente
 * validi, e chi lo subisce non ha modo di capire perché: il suo codice è quello,
 * ed è il sistema a dire che non esiste.
 */
export const CODICE_FISCALE_PEZZI = [
    [/[A-Z]/, 6], // le consonanti del cognome e del nome
    [OMOCODIA, 2], // le due cifre dell'anno
    [/[ABCDEHLMPRST]/, 1], // il mese, una lettera su dodici
    [OMOCODIA, 2], // il giorno, con il +40 delle donne
    [/[A-Z]/, 1], // la lettera del comune di nascita
    [OMOCODIA, 3], // le tre cifre del comune
    [/[A-Z]/, 1], // il carattere di controllo
];
export const CODICE_FISCALE = new RegExp(`^${CODICE_FISCALE_PEZZI.map(([forma, n]) => `${forma.source}{${n}}`).join('')}$`);
export const CODICE_FISCALE_LUNGHEZZA = CODICE_FISCALE_PEZZI.reduce((t, [, n]) => t + n, 0);
/**
 * Il luogo di un ritiro o di una consegna: sette misure scritte **due volte
 * ciascuna**, perché creare un luogo e correggerlo sono due classi diverse.
 *
 * `CreateTransferLocationDto` e `UpdateTransferLocationDto` sono la stessa
 * scheda vista in due momenti, e nel server sono quattordici decoratori con
 * dentro sette numeri ripetuti. Non divergono oggi. Il giorno in cui uno dei due
 * cambia — perché un indirizzo lungo è stato rifiutato e qualcuno alza il
 * duecento — il difetto che ne esce è quello di §60: un luogo si crea e poi non
 * si riesce più a salvarlo, o il contrario, e il rifiuto parla di lunghezza
 * mentre chi lo legge sta guardando un indirizzo che gli sembra normale.
 *
 * Il pannello, dal canto suo, non aveva **nessuno** di questi sette numeri:
 * dodici caselle del modulo del luogo e sei di quello dei colli, e l'unica con
 * un tetto era il «perché» della dismissione, che ce l'ha da §163. Si batte
 * quanto si vuole, e il rifiuto arriva a modulo compilato — con
 * `forbidNonWhitelisted` addosso, per l'intera richiesta e senza dire su quale
 * casella.
 *
 * ## `q` della ricerca, che era più corta di quello che cerca
 *
 * `SearchLocationsDto.q` valeva 120, e cerca **anche** sul nome, che vale 160.
 * Incollare nella casella di ricerca un nome di luogo fra 121 e 160 caratteri —
 * il gesto più naturale che ci sia, copiare il nome per ritrovarlo — restituiva
 * un 400 sulla lunghezza invece del luogo. Qui `q` è legata al più lungo dei
 * campi su cui passa: una ricerca non può essere più stretta di quello che
 * indicizza.
 */
export const LUOGO = {
    /** 2–160: il nome del posto, che è anche il più lungo dei campi cercabili. */
    name: { min: 2, max: 160 },
    addressStreet: INDIRIZZO.street,
    addressCity: INDIRIZZO.city,
    contactName: { max: 120 },
    contactPhone: { max: TELEFONO_MAX_SCRITTO },
    openingHours: { max: 200 },
    note: { max: 1000 },
};
/**
 * La ricerca **dei luoghi**, che è un'altra cosa dalla ricerca di un elenco.
 *
 * `GET /logistics/locations/search` è il selettore che si apre dentro il modulo
 * della spedizione, e cerca sul nome del posto: non può essere più corta del più
 * lungo dei campi su cui passa. La casella in cima all'elenco dei luoghi non è
 * questa — quella manda `search` di `PaginationQueryDto`, e vale `RICERCA_MAX`.
 * Sono due rotte diverse con due tetti diversi, e non sono nemmeno nello stesso
 * programma: questo selettore è un foglio della app (`FoglioLuoghi`), la casella
 * in cima all'elenco è un componente del pannello. Nel pannello, di caselle che
 * vogliano un tetto diverso da `RICERCA_MAX` non ce n'è nessuna.
 *
 * ## Quante righe tornano, che è la seconda metà dello stesso `Math.min`
 *
 * `search` tronca a cinquanta e non lo scrive da nessuna parte nella risposta:
 * `meta.total` è il numero delle righe tornate, cioè cinquanta, non quante ce ne
 * sarebbero. Cinquanta righe tonde sono quasi sempre una risposta tagliata, e il
 * foglio della app lo dice — sotto l'elenco compare la riga che invita a
 * stringere — ma lo diceva ricopiando il numero. Il giorno in cui il server
 * tagliasse a trenta, quella riga smetterebbe di comparire proprio quando serve:
 * l'elenco sarebbe monco e a schermo non ci sarebbe più niente a dirlo. Chi cerca
 * il proprio capannone, non lo trova e conclude che non è censito, ne apre un
 * secondo con lo stesso nome — che è il difetto da cui nasce il vincolo di
 * unicità sul nome.
 */
export const LUOGO_RICERCA = {
    min: 2,
    max: LUOGO.name.max,
    /** Quante righe torna il selettore: quelle chieste se tace, e il tetto che vince. */
    righe: { predefinite: 20, max: 50 },
};
/**
 * Il CAP: cinque cifre, e il cinque scritto una volta sola.
 *
 * La forma stava due volte nel server — creazione e correzione — e nel pannello
 * non stava affatto: la casella accettava venti cifre e il rifiuto arrivava dopo.
 */
export const CAP_CIFRE = 5;
export const CAP = new RegExp(`^\\d{${CAP_CIFRE}}$`);
/**
 * La sigla della provincia e il codice della nazione: **due** forme identiche, e
 * restano due.
 *
 * Sono la stessa espressione carattere per carattere, e sarebbe naturale
 * scriverne una sola. Sono però due vocabolari diversi che per conto proprio
 * dicono entrambi «due lettere»: la targa della provincia lo dice per l'Italia,
 * l'ISO 3166-1 alpha-2 lo dice per il mondo. Il giorno in cui la nazione
 * diventasse un elenco chiuso di codici veri — che è l'unico modo di impedire
 * `XX` — la forma condivisa andrebbe spaccata in due proprio mentre la si sta
 * usando in tre posti. È la stessa ragione per cui `CODICE_RUOLO_MAX` non
 * riusa il quaranta di `PERSONA.matricola`: uguale non vuol dire legato.
 *
 * Quello che si toglie di mezzo è la ripetizione **vera**: dodici copie della
 * forma — quattro per il luogo, sei per la sede, e le due che il pannello si
 * riscriveva per conto suo — e le caselle che una misura non ce l'avevano.
 *
 * Il pannello le sue due copie le scriveva con `[A-Za-z]`, cioè accettava «po»
 * minuscolo; il server accetta anche lui «po», ma perché lo alza a maiuscolo
 * prima di guardarlo. Chi lega la casella a questa forma deve alzarlo anche lui,
 * altrimenti il pannello diventa più severo del server su una cosa che il server
 * perdona apposta.
 */
export const PROVINCIA_LETTERE = 2;
export const PROVINCIA = new RegExp(`^[A-Z]{${PROVINCIA_LETTERE}}$`);
export const NAZIONE_LETTERE = 2;
export const NAZIONE = new RegExp(`^[A-Z]{${NAZIONE_LETTERE}}$`);
/**
 * La sede: il nome, e l'indirizzo che è quello di tutti.
 *
 * Le sue misure erano scritte **cinque** volte: `CreateHeadquarterDto`,
 * `UpdateHeadquarterDto`, e una terza volta in `PrimoAmministratoreDto`, che al
 * primo avvio la sede la crea. Quella terza copia è la più pericolosa delle tre,
 * perché è l'unica che non sta accanto alle altre due: chi cambiasse il nome
 * della sede da 160 a 200 troverebbe le due classi vicine e non la procedura di
 * avvio, che continuerebbe a rifiutare a 160 un nome che il pannello accetta.
 */
export const SEDE = {
    name: { min: 2, max: 160 },
    addressStreet: INDIRIZZO.street,
    addressCity: INDIRIZZO.city,
    phoneNumber: { max: TELEFONO_MAX_SCRITTO },
};
/**
 * Il reparto: il nome, e il codice con la forma costruita dalla misura.
 *
 * Stessa storia della sede — due classi più il primo avvio — e in più il codice,
 * la cui forma stava in una costante privata del DTO con il trenta scritto due
 * volte: dentro la regex e dentro il messaggio d'errore accanto. Due numeri per
 * un fatto, a dieci righe di distanza, e il messaggio è quello che la persona
 * legge.
 *
 * La forma è larga apposta — lettere, cifre, punti, trattini — perché i codici
 * dei reparti del vecchio gestionale ci passino senza essere riscritti.
 */
export const REPARTO = {
    name: { min: 2, max: 120 },
    code: { min: 1, max: 30 },
};
export const CODICE_REPARTO = new RegExp(`^[A-Za-z0-9._-]{${REPARTO.code.min},${REPARTO.code.max}}$`);
/**
 * La rubrica: chi non lavora qui ma bisogna poterlo chiamare.
 *
 * Il tetto della posta lo prende da `EMAIL_MAX`, che è più largo dei duecento
 * che aveva: vedi là il perché.
 *
 * Il telefono qui aveva una **forma** e non una misura, ed era l'unico dei cinque
 * telefoni del sistema a comportarsi così: `TELEFONO_E164` stava sotto queste
 * righe, e accanto c'era scritto che non era una svista — qui il numero serve a
 * comporre una chiamata, non a essere letto.
 *
 * La ragione era giusta e la forma era sbagliata. E.164 vieta lo zero iniziale, e
 * i fissi italiani in forma nazionale cominciano tutti per zero: sulle righe vere
 * quella forma rifiuta 159 telefoni d'ufficio su 159. Adesso i cinque telefoni si
 * comportano tutti allo stesso modo — si ripuliscono e passano da
 * `TELEFONO_COMPONIBILE`, che sta in `anagrafica.ts` con la misura accanto. La
 * pulizia che li precede — via spazi, punti, trattini, parentesi, barre — resta
 * quella di prima e per lo stesso motivo: `+39 055 123` e `+39055123` non devono
 * diventare due righe della stessa persona.
 */
export const CONTATTO = {
    name: { min: 2, max: 160 },
    company: { max: 160 },
    role: { max: 120 },
    note: { max: 1000 },
};
/**
 * Il mezzo: il nome che gli si dà, la targa, e i numeri che dicono se ci sta.
 *
 * Quindici misure scritte due volte — `CreateVehicleDto` e `UpdateVehicleDto` —
 * e la targa scritta **quattro** volte: due come forma e due come parole dentro
 * il messaggio che le sta accanto («4-12 fra lettere e cifre»). Il pannello ne
 * aveva una quinta copia della forma, in `MezziView`, dove serve a decidere se
 * il bottone «Salva» si accende; e le sue caselle il tetto lo portavano a mano.
 *
 * `anno` non è una lunghezza ed è qui lo stesso, perché è la stessa specie di
 * numero: 1950 è più vecchio di qualunque cosa sia mai stata targata qui dentro,
 * 2100 è il paraurti contro chi scrive l'anno di oggi con una cifra di troppo.
 *
 * Quanti usi può avere un mezzo qui non c'è, e non è una dimenticanza: lo dice
 * `VehicleUsage`, e un numero accanto sarebbe la copia del conto dei suoi valori
 * — il giorno che ne nasce un terzo, la copia resta due e il mezzo che li vuole
 * tutti e tre viene rifiutato senza che nessuno capisca perché.
 */
export const MEZZO = {
    displayName: { min: 2, max: 120 },
    brand: { max: 80 },
    model: { max: 80 },
    note: { max: 1000 },
    posti: { min: 1, max: 120 },
    anno: { min: 1950, max: 2100 },
    /** Le dotazioni: `TOWBAR`, `FRIDGE`. Quante ce ne stanno, e quanto lunga una. */
    dotazioni: { max: 50, voce: { max: 40 } },
    /** La finestra di «che cosa scade presto»: un anno, e non di più. */
    scadenzeEntroGiorni: { min: 0, max: 365 },
};
/**
 * La targa: la forma costruita dalla misura, così che il messaggio d'errore non
 * possa dire un numero diverso da quello che la forma pretende.
 */
export const TARGA_CARATTERI = { min: 4, max: 12 };
export const TARGA = new RegExp(`^[A-Z0-9]{${TARGA_CARATTERI.min},${TARGA_CARATTERI.max}}$`);
/**
 * La saletta: il nome, il piano, e quante persone ci stanno.
 *
 * `capienza.max` **non c'era da nessuna parte**. Il server chiedeva soltanto che
 * fosse almeno uno, e il pannello lasciava scrivere sei cifre nella casella: una
 * saletta da novecentonovantanovemila posti passava, e poi si presentava nel
 * modulo della riunione come la sala che ci sta sempre.
 *
 * Il tetto è nato a cinquecento, «largo apposta», con l'idea che quello che si
 * vuole fermare sia l'incollaggio andato male e non una scelta di chi arreda.
 * È stato stretto a cento su decisione di Cosimo, e la ragione vale più del
 * numero: **un tetto vicino al vero ferma anche l'errore di una cifra sola**.
 * Cinquanta battuto come cinquecento è lo sbaglio che càpita davvero — la mano
 * scivola sullo zero, la sala esiste, il numero è plausibile, e nessuno se ne
 * accorge finché non si cerca un posto per una riunione da duecento e il modulo
 * la propone. Con il tetto a cinquecento quel `500` passa liscio; con il tetto a
 * cento suona. Un tetto messo così lontano da non poter essere raggiunto per
 * sbaglio non è un limite, è una decorazione.
 *
 * Cento è sopra la sala più grande che l'azienda ha, quindi non toglie niente a
 * nessuno oggi. Se un giorno servisse un auditorium più grande, si alza qui —
 * ma si alzi fino a poco sopra quello che serve, non fino a un numero tondo.
 */
export const SALA = {
    name: { min: 2, max: 120 },
    floor: { max: 40 },
    capienza: { min: 1, max: 100 },
};
/**
 * Il tipo di collo: il nome, e il codice con la forma costruita dalla misura.
 *
 * Il codice si scrive una volta e non si cambia più, quindi la forma sta in una
 * classe sola — ma il pannello la casella ce l'ha lo stesso, e senza tetto.
 *
 * Vale la pena dire in che cosa **non** somiglia a `CODICE_RUOLO`, che a prima
 * vista è lo stesso oggetto: là la prima posizione deve essere una lettera, qui
 * no, quindi `_1` e `9x` sono codici di collo validi. Non è stato stretto di mia
 * iniziativa: le righe che ci sono già non vengono rivalidate, ma quelle che
 * l'importazione porta dentro sì, e non so che cosa contenga la colonna in
 * produzione. Resta scritto perché la prossima persona non lo scopra da un
 * rifiuto.
 */
export const TIPO_COLLO_MIN = 2;
export const TIPO_COLLO_MAX = 30;
export const TIPO_COLLO = {
    name: { min: 2, max: 80 },
    code: { min: TIPO_COLLO_MIN, max: TIPO_COLLO_MAX },
};
export const CODICE_COLLO = codiceMacchina(TIPO_COLLO_MIN, TIPO_COLLO_MAX);
const SPEDIZIONE_QUANTITA_MAX = 9999;
/**
 * La spedizione: sei misure, e la solita coppia `Create`/`Update` che le scrive
 * due volte ciascuna.
 *
 * ## I due nomi, che valgono più del doppio di un nome
 *
 * `depositaryName` e `recipientName` valgono 255, e sono nomi di persone —
 * chi consegna la merce e chi la riceve, i due che ci sono quel giorno. Il nome
 * di una persona *dentro il sistema* vale invece 120 (`PERSONA.displayName`).
 * Sono due tetti diversi per la stessa cosa scritta a mano: una persona.
 *
 * Non ho pareggiato i due numeri, e la ragione non è che 255 sia giusto — è che
 * nessuno dei due è **ancorato**. Nello schema quelle colonne non hanno una
 * larghezza: sono `text`, quindi il 255 non viene dal database, viene da
 * un'abitudine. Stringerlo a 120 vorrebbe dire rifiutare righe che
 * l'importazione dal vecchio potrebbe portare dentro, e non so che cosa
 * contengano. Resta scritto perché la prossima persona veda che è una scelta
 * rimasta aperta, non un accordo.
 */
export const SPEDIZIONE = {
    /** Ordine cliente, DDT, comunque lo chiami la carta. */
    reference: { max: 120 },
    note: { max: 1000 },
    /** 255 e non 120: vedi sopra, non è un accordo con `PERSONA.displayName`. */
    depositaryName: { max: 255 },
    recipientName: { max: 255 },
    /** La merce a parole, quella che chi carica deve riconoscere sul bancale. */
    goods: { max: 1000 },
    /** La nota sul singolo collo, che è un'altra cosa dalla nota della spedizione. */
    packageNote: { max: 300 },
    /**
     * Quanti colli, e quanti pezzi per collo.
     *
     * Il minimo di uno è la regola che tiene fuori le righe che il vecchio aveva:
     * spedizioni senza niente dentro, esistite solo perché si potesse scrivere una
     * data. Il cinquanta e il novemilanovecentonovantanove non difendono da un
     * errore di battitura — nessuno spedisce cinquantun colli per sbaglio — ma da
     * un incolla andato male, che è l'unico modo in cui quei numeri si superano.
     */
    colli: { min: 1, max: 50 },
    /**
     * `cifre` non e' una misura in piu': e' la stessa, contata in caratteri.
     *
     * La casella dei pezzi sul telefono aveva `maxLength={4}`, che e' il
     * novemilanovecentonovantanove qui accanto riscritto in un'altra unita' - e in
     * una forma dove il numero non compare piu'. Alzare il massimo lasciava
     * indietro la casella, e la casella non lo avrebbe detto: avrebbe soltanto
     * smesso di far scrivere la quinta cifra.
     */
    quantita: {
        min: 1,
        max: SPEDIZIONE_QUANTITA_MAX,
        cifre: String(SPEDIZIONE_QUANTITA_MAX).length,
    },
    /**
     * Il peso di un collo: il server ha soltanto `@Min(0)`, un tetto non ce l'ha.
     *
     * Le otto battute sono quelle che la casella del telefono concede, e bastano
     * a scrivere dodicimila chili con due decimali. Un collo che ne dichiara
     * dieci milioni e' un incolla andato male. Sta scritto qui per la ragione
     * opposta a tutte le altre misure di questo file - non perche' il server lo
     * imponga, ma perche' chi un giorno gli mettesse un `@Max` trovi gia'
     * scritto quello che la app accetta.
     */
    peso: { min: 0, cifre: 8 },
};
/**
 * L'avviso: quel che il pannello scrive e il telefono riceve.
 *
 * Il corpo qui è **solo quello in chiaro**. Un avviso ha due formati e due
 * tetti: `PLAIN` è `corpoPlain`, `COMUNICATO` è `LIMITI_COMUNICATO.maxCorpo` in
 * `comunicato.ts`, accanto alla grammatica che lo governa. Non li ho uniti, e
 * non è pigrizia: il quattromila è la misura contro cui sono stati costruiti
 * tutti i mittenti automatici e tutti i client già installati, e alzarlo
 * vorrebbe dire cambiare una promessa fatta a loro.
 *
 * `destinatari` è quante persone si possono nominare a mano in un invio:
 * duemila, cioè quattro volte l'organico. Non difende dal dito che scivola,
 * difende dal client difettoso che infila l'anagrafica intera dentro `userIds`
 * invece di dire `everyone: true`.
 */
export const AVVISO = {
    titolo: { min: 3, max: 200 },
    corpoPlain: { max: 4000 },
    destinatari: { max: 2000 },
    /** L'etichetta della campagna, così che un invio solo si racconti come uno. */
    chiaveCampagna: { max: 80 },
};
/**
 * Quante righe per pagina può chiedere l'elenco degli avvisi: **la metà** di
 * `PER_PAGE_MAX`.
 *
 * La differenza c'era già, e non era scritta da nessuna parte — il che la
 * faceva somigliare a una dimenticanza. Non lo è: questo elenco consegna il
 * corpo intero di ogni avviso, che può arrivare a ventimila caratteri, e con
 * esso le fotografie incorporate. Duecento righe così sono qualche megabyte su
 * una scheda telefonica, per una schermata che ne mostra dieci per volta.
 *
 * Il nome dice gli avvisi e non «pagina piccola» apposta: è l'unico elenco del
 * sistema che non segue `PER_PAGE_MAX`, e se un giorno ne nascesse un secondo
 * con la stessa ragione, i due numeri vanno guardati insieme prima di
 * unificarli.
 */
export const AVVISI_PER_PAGINA_MAX = 100;
/**
 * Quanti file si possono attaccare a una cosa sola, e quanto è lunga la ricevuta
 * che ne certifica uno.
 *
 * Il cinquanta non è una scelta di questo file: è il `max` con cui il catalogo
 * delle impostazioni delimita `storage.max_attachments_per_item`, cioè il più
 * alto che un amministratore possa metterci. Il tetto **vero** è la sua
 * manopola, che vale dieci di suo e la applica il servizio; qui c'è solo il muro
 * oltre il quale la richiesta non viene nemmeno letta. Erano due numeri uguali
 * in due file che non si conoscono: portare la manopola a cento avrebbe prodotto
 * un amministratore convinto di averlo fatto e un DTO che continuava a
 * rifiutare al cinquantunesimo file.
 *
 * La ricevuta è un'affermazione firmata da questo server su un file già
 * caricato — non un id, e non una cosa che qualcuno digita. I 1024 caratteri
 * sono il muro contro un corpo di richiesta gonfiato, non una misura del
 * formato.
 */
export const ALLEGATI_PER_ELEMENTO_MAX = 50;
export const RICEVUTA_ALLEGATO_MAX = 1024;
/**
 * Il telefono che si annuncia: quel che l'app dice di sé quando si registra per
 * ricevere le notifiche.
 *
 * `installationId` è quello che fa **di un telefono una riga**: lo genera l'app
 * una volta per installazione e se lo tiene attraverso i cambi di indirizzo
 * push. La tentazione è di usare come chiave il gettone push, ed è così che la
 * tabella dei dispositivi del vecchio si è riempita di indirizzi morti.
 *
 * Le tre misure in fondo — versione dell'app, versione del sistema, modello —
 * sono l'unica cosa che si sa di un apparecchio il giorno che una notifica non
 * arriva.
 */
export const DISPOSITIVO = {
    installationId: { min: 8, max: 128 },
    pushToken: { max: 512 },
    unifiedpushEndpoint: { max: 512 },
    appVersion: { max: 40 },
    osVersion: { max: 40 },
    deviceModel: { max: 80 },
    /**
     * Da **quale app** viene questo token.
     *
     * Un bundle identifier di iOS (`it.beste.hr`) o il nome del pacchetto Android: 128 come
     * l'identificativo d'installazione, perché è la stessa famiglia di stringhe — nomi a punti
     * che qualcun altro ha deciso.
     */
    bundleId: { max: 128 },
};
/**
 * La chat: undici misure, e quasi tutte scritte due volte.
 *
 * Il nome della conversazione sta in `CreateGroupDto` e in
 * `RenameConversationDto`; l'elenco dei partecipanti in `CreateGroupDto` e in
 * `AddParticipantsDto`; il corpo del messaggio in `SendMessageDto` e in
 * `EditMessageDto`. Poi c'è una terza copia del corpo, che sul server non sta:
 * è `LUNGHEZZA_MASSIMA` in `BarraScrittura.tsx`, cioè il punto in cui la
 * tastiera smette di accettare caratteri. E una quarta, la più insidiosa delle
 * quattro perché non somiglia affatto a un tetto: il `max` con cui il catalogo
 * delle impostazioni delimita `chat.max_message_length`. Se quella manopola
 * potesse salire sopra questo numero, un amministratore la alzerebbe e i
 * messaggi continuerebbero a essere rifiutati da un `@MaxLength` che lui non
 * può vedere.
 *
 * `corpo.max` è il muro, non la regola: la regola è la manopola, che di suo vale
 * quattromila e la applica il servizio con una frase sua. Il valore della
 * manopola il client non lo conosce, quindi si ferma al muro e lascia che a
 * spiegare il resto sia il rifiuto del server.
 *
 * `silenzioMinuti` sono i minuti di silenzio di una conversazione, e il massimo
 * è un anno: 525.600. Zero vuol dire «torna a suonare» — un valore, non una
 * seconda rotta, perché silenziare e togliere il silenzio sono lo stesso
 * interruttore, e un'interfaccia che deve scegliere fra due indirizzi sbaglia
 * proprio sul ritorno.
 *
 * `ricerca.limite` porta anche il **predefinito** del server, che un tetto non è
 * e sta qui lo stesso: la schermata di ricerca dell'app lo aveva scritto dentro
 * un commento in prosa («ne consegna 30 se non gli si dice niente»), e una prosa
 * che nomina un numero del server è una copia come tutte le altre — solo che non
 * la trova nessuno.
 */
export const CHAT = {
    nome: { min: 2, max: 120 },
    partecipanti: { min: 1, max: 500 },
    corpo: { min: 1, max: 20_000 },
    /** L'id che il client dà al messaggio, perché un rinvio non lo pubblichi due volte. */
    clientId: { max: 64 },
    silenzioMinuti: { min: 0, max: 525_600 },
    messaggi: { limite: { max: 200, predefinito: 50 } },
    ricerca: { min: 2, max: 200, limite: { max: 100, predefinito: 30 } },
};
/**
 * La nota spese: quattro misure che la coppia `Create`/`Update` scriveva due
 * volte ciascuna, e le due forme che nessuno aveva mai messo in comune.
 *
 * `importoCaratteri` non è un tetto del server — il server sull'importo non ne
 * ha nessuno, ha `@IsPositive` e due decimali. È il tetto della **casella**, e
 * sta qui perché la app lo scrive in due schermate («nuova spesa» e
 * «approvazione con taglio») e nessuna delle due sapeva dell'altra. Dieci
 * caratteri sono `9999999.99`: dieci milioni di euro su una ricevuta di cena.
 */
export const SPESA = {
    descrizione: { max: 1000 },
    esercente: { max: 200 },
    /** La motivazione di chi approva — obbligatoria quando l'importo si taglia. */
    notaApprovazione: { max: 500 },
    /** Quante cifre entrano nella casella dell'importo: non è il server, è la tastiera. */
    importoCaratteri: 10,
};
/**
 * La partita IVA italiana: undici cifre, e il numero scritto **una volta sola**.
 *
 * Era `/^\d{11}$/` in due classi, e in tutti e due i messaggi l'undici tornava
 * scritto a parole: «La partita IVA è di undici cifre». Tre copie dello stesso
 * fatto, di cui due invisibili a qualunque ricerca del numero.
 */
export const PARTITA_IVA_CIFRE = 11;
export const PARTITA_IVA = new RegExp(`^\\d{${PARTITA_IVA_CIFRE}}$`);
/**
 * Il periodo di paga su cui un rimborso viaggia: `2026-08`, oppure la
 * tredicesima, la quattordicesima e il conguaglio.
 *
 * Due copie carattere per carattere, e su due sponde diverse: il DTO del server
 * e il controllo che accende il pulsante in `NoteSpeseView.vue`. Sono la specie
 * peggiore di copia, perché il pannello che si allontanasse dal server non
 * darebbe un errore — darebbe un pulsante spento su un periodo valido, oppure un
 * pulsante acceso e un 400 dopo.
 *
 * Il `CU` è il conguaglio, e il `13`/`14` sono le mensilità aggiuntive: non sono
 * mesi, e per questo il periodo è una stringa e non una data.
 */
export const PERIODO_PAGA = /^\d{4}-(0[1-9]|1[0-4]|CU)$/;
/**
 * Il tipo di nota spese, che di misure ne ha tre e le scriveva tutte a mano.
 *
 * Il codice qui **non** vuole la lettera iniziale (vedi `codiceMacchina`), e il
 * nome vale cento — non ottanta come il capo di vestiario, non centoventi come
 * il tipo di giustificativo. Tre cataloghi affiancati nello stesso pannello con
 * tre tetti diversi sul proprio nome: non li ho pareggiati, perché pareggiarli
 * vorrebbe dire stringerne due su righe che in produzione ci sono già.
 */
export const TIPO_SPESA = {
    code: { min: 2, max: 30 },
    name: { min: 2, max: 100 },
};
export const CODICE_TIPO_SPESA = codiceMacchina(TIPO_SPESA.code.min, TIPO_SPESA.code.max);
/**
 * Il tipo di giustificativo: il catalogo delle assenze.
 *
 * `approvazioni` è la sola misura di questo file che non conta caratteri ma
 * **firme**: zero vuol dire che l'assenza si concede da sé, uno che la firma il
 * responsabile, due che dopo di lui firma l'ufficio del personale. Oltre il due
 * non esiste una terza catena, e il `@Max(2)` non è un tetto di prudenza — è la
 * fine dell'elenco.
 */
export const TIPO_GIUSTIFICATIVO = {
    code: { min: 2, max: 50 },
    name: { min: 2, max: 120 },
    descrizione: { max: 500 },
    /** 0 = da sé, 1 = il responsabile, 2 = il responsabile e poi l'ufficio. */
    approvazioni: { min: 0, max: 2 },
};
export const CODICE_TIPO_GIUSTIFICATIVO = codiceParlante(TIPO_GIUSTIFICATIVO.code.min, TIPO_GIUSTIFICATIVO.code.max);
/**
 * La richiesta di assenza: la nota che chi la chiede scrive, e la ricevuta del
 * certificato.
 *
 * La ricevuta è `RICEVUTA_ALLEGATO_MAX`, la stessa dell'avviso: là ce n'è un
 * elenco, qui una sola, ma è lo stesso oggetto firmato dallo stesso servizio con
 * la stessa scadenza. Erano due `@MaxLength(1024)` scritti a mano in questo
 * modulo e un terzo, ancorato, nelle notifiche.
 */
export const GIUSTIFICATIVO = {
    nota: { max: 2000 },
    /**
     * La nota di chi approva, che non è la nota di chi chiede.
     *
     * Sono due campi diversi con due autori diversi e infatti due misure diverse:
     * duemila per chi racconta perché sarà assente, mille per chi firma. Il
     * secondo era un `@MaxLength(1000)` nudo in `ApproveJustificationDto`, mentre
     * il rifiuto lì accanto era già `MOTIVO.ampio` — cioè nella stessa classe di
     * decisioni una metà era ancorata e l'altra no. Il gemello sulla spesa è
     * `SPESA.notaApprovazione`, e resta una misura sua: uguale non vuol dire
     * legato.
     */
    notaApprovazione: { max: 1000 },
};
/**
 * La trasferta: la destinazione, e basta.
 *
 * Il resto lo tengono già `SCOPO_TRASFERTA_MAX` (il «motivo» che vale mille) e
 * `NAZIONE` (la sigla di due lettere). Il paese qui è la stessa trappola di §B1:
 * il server scriveva `[A-Za-z]{2}` **dopo** un `@Transform` che alza a maiuscolo,
 * cioè una permissività che non serviva a niente perché a quel punto il
 * minuscolo non poteva più esserci.
 */
export const TRASFERTA = {
    destinazione: { min: 2, max: 200 },
};
/**
 * Il capo di vestiario, e la forma di una taglia.
 *
 * `TAGLIA` era scritta **tre** volte identica — nel DTO del capo, in quello
 * della richiesta e nel pannello — e in nessuno dei tre punti c'era scritto
 * perché ammetta il punto, la barra e il trattino: perché le taglie vere sono
 * anche `42/44`, `M-L` e `2.5`, non solo `M`.
 *
 * `taglie.max` (quaranta) stava in tre `@ArrayMaxSize(40)` e in nessuna casella:
 * il pannello le taglie le fa scrivere separate da virgola dentro un `InputText`
 * senza tetto, quindi quarantuno taglie incollate facevano tornare un 400 che a
 * schermo si legge «non si è salvato», non «sono troppe».
 */
export const CAPO = {
    code: { min: 2, max: 30 },
    name: { min: 2, max: 80 },
    taglie: { min: 1, max: 40 },
};
export const CODICE_CAPO = codiceMacchina(CAPO.code.min, CAPO.code.max);
export const TAGLIA = /^[A-Z0-9]+([./-][A-Z0-9]+)*$/;
/**
 * La richiesta di divisa: quanti capi ci stanno dentro, e la nota.
 *
 * `nota.max` era `@MaxLength(1000)` in **quattro** classi di questo stesso file
 * — la si scrive quando si chiede, quando si corregge, quando si approva e
 * quando si consegna — e `capi.max` era `@ArrayMaxSize(30)` in tre. Nessuno dei
 * due arrivava fino alla app: `SchermataNuovaDivisa` il mille se lo riscriveva
 * per conto proprio.
 */
export const RICHIESTA_DIVISA = {
    capi: { min: 1, max: 30 },
    nota: { max: 1000 },
};
/**
 * Il ruolo: il nome che si legge, la descrizione e quanti permessi ci stanno
 * dentro.
 *
 * Il codice ce l'ha già `CODICE_RUOLO`, ed era l'unico dei quattro che il
 * pannello sapesse misurare: in `RuoliView` il nome e la descrizione non
 * avevano `maxlength` affatto. Un nome di ottantun caratteri tornava indietro
 * come un 400 sull'intera richiesta — cioè come un salvataggio che non succede
 * e non dice perché.
 *
 * `permessi.max` non è prudenza sull'array: duecento è più di quanti permessi
 * il catalogo ne contenga, e serve a fermare un corpo di richiesta gonfiato,
 * non un ruolo grande.
 */
export const RUOLO = {
    name: { min: 2, max: 80 },
    descrizione: { max: 500 },
    permessi: { max: 200 },
};
/**
 * Un'impostazione: la chiave, il valore, e quante se ne cambiano in un colpo.
 *
 * Il valore viaggia come testo comunque, perché il tipo lo dichiara il
 * catalogo e non un decoratore — qui c'è solo il muro oltre il quale la
 * richiesta è un abuso, non un'impostazione lunga. `ImpostazioniView` le sue
 * due caselle le scriveva senza alcun tetto, e il salvataggio di un gruppo è
 * tutto-o-niente: un valore troppo lungo non faceva perdere un campo, faceva
 * perdere la pagina.
 */
export const IMPOSTAZIONE = {
    chiave: { max: 120 },
    valore: { max: 2000 },
    /** Quante impostazioni entrano in un solo salvataggio. */
    gruppo: { max: 100 },
};
/**
 * L'indirizzo hardware di un cartello, e quanto è lungo scritto.
 *
 * La forma era privata del DTO del cartello, e `CartelliView` scriveva
 * `maxlength="17"`: la lunghezza della forma giusta **contata a mano**, non la
 * forma. Contarla a mano vuol dire che il giorno che la notazione cambiasse la
 * casella resterebbe lunga diciassette e nessuno se ne accorgerebbe — finché
 * la stessa targhetta non entra due volte con due chiavi diverse, che è
 * esattamente ciò contro cui una colonna unica non può niente.
 *
 * `MAC_CARATTERI` è **derivato** dalla regola, non copiato: sei coppie più i
 * cinque due punti in mezzo.
 */
export const MAC_COPPIE = 6;
export const MAC = new RegExp(`^[0-9A-F]{2}(:[0-9A-F]{2}){${MAC_COPPIE - 1}}$`);
export const MAC_CARATTERI = MAC_COPPIE * 3 - 1;
/**
 * Il cartello e-ink appeso fuori dalla saletta.
 *
 * `millivolt` e `pressioneMs` non sono tetti di caselle — nessuno li batte a
 * mano, li manda il tag — ma sono i due numeri che dicono quando una lettura è
 * una lettura e quando è un guasto, e stavano scritti solo dentro il DTO. Sotto
 * i duemila millivolt il tag è spento, sopra i cinquemila non è una batteria; e
 * una pressione più lunga di un minuto è un pulsante incastrato, non una
 * riunione che finisce.
 */
export const CARTELLO = {
    etichetta: { max: 120 },
    firmware: { max: 60 },
    millivolt: { min: 2000, max: 5000 },
    pressioneMs: { min: 0, max: 60_000 },
};
/**
 * La riunione, la prenotazione della sala e quella del mezzo: sono lo stesso
 * oggetto, e quindi hanno le stesse misure.
 *
 * Titolo e descrizione stavano scritti due volte ciascuno — `Create` e
 * `Update` — e gli invitati altre due. Il motivo dell'annullamento no: quello
 * era già `MOTIVO.breve`, ed è il pezzo di questo DTO che qualcuno aveva
 * ancorato per primo.
 *
 * `invitati.max` è duecento come i permessi di un ruolo, e i due numeri non
 * sono legati: uguale non vuol dire legato. Duecento invitati è già una
 * convocazione che nessuno leggerà; duecento permessi è più di quanti il
 * catalogo ne abbia.
 */
export const EVENTO = {
    titolo: { min: 2, max: 200 },
    descrizione: { max: 2000 },
    invitati: { max: 200 },
    /**
     * L'invitato che in azienda non lavora (riga I09): un indirizzo di posta e, se
     * si conosce, un nome.
     *
     * `email.max` è `EMAIL_MAX` come in anagrafica: è lo stesso dato, e il numero è
     * dello standard, non nostro. `nome.max` è centoventi come `displayName`,
     * perché il nome di un fornitore non è più corto di quello di un collega, e
     * `min: 1` vale **dopo il taglio degli spazi**: in banca dati un nome fatto di
     * soli spazi è già rifiutato, e un DTO che lo lasciasse passare farebbe
     * arrivare l'errore dal database invece che dal modulo, dove si legge.
     */
    invitatoEsterno: {
        email: { max: EMAIL_MAX },
        nome: { min: 1, max: 120 },
        /**
         * Quanti esterni al massimo per riunione: venti, decisione di Cosimo del 2026-10-19
         * (`da-fare.md` §5 nonies). Basta per qualunque riunione vera e ferma l'incollare una lista
         * di posta. Vale sui presenti, non sulle righe tolte; lo leggono server, Cruscotto e app.
         */
        max: 20,
    },
};
/**
 * L'agenda che il telefono riporta al server: le misure del giro di
 * sincronizzazione.
 *
 * `impronta.max` è sessantaquattro perché sessantaquattro cifre esadecimali
 * sono uno sha256 intero, ed era scritto due volte nello stesso file — sul giro
 * di controllo e sul caricamento. `impronta.cifre` è **sedici**, ed è il numero
 * più delicato di tutto il modulo: è quanto ne conserva il database
 * (`left(encode(…), 16)`) ed è quanto ne manda la app. Erano due copie a mano
 * su due sponde diverse, con un commento in ognuna che diceva all'altra di
 * restare uguale — cioè il caso di §60 preso in flagrante, perché il giorno che
 * una delle due si muovesse non ci sarebbe un errore: ci sarebbe un confronto
 * che non coincide mai più, e quindi un orizzonte intero rimandato ogni volta,
 * per sempre, in silenzio.
 *
 * `ack.max` era `@ArrayMaxSize(500)` due volte **nella stessa classe** — le
 * voci applicate e quelle fallite — e non c'era scritto da nessuna parte che
 * fossero lo stesso tetto per la stessa ragione.
 */
export const AGENDA_DISPOSITIVO = {
    /** Il riferimento opaco all'evento sul telefono: un hash, non l'identificativo vero. */
    ref: { min: 8, max: 128 },
    impronta: { max: 64, cifre: 16 },
    /** L'uid di una voce di coda, come lo scrive il calendario del dispositivo. */
    uid: { max: 200 },
    /** Quante ricevute (applicate o fallite) stanno in un solo giro. */
    ack: { max: 500 },
    /**
     * Quanto può portare un solo caricamento.
     *
     * Novanta giorni di agenda fitta stanno abbondantemente sotto questi due
     * numeri: superarli non vuol dire «agenda grande», vuol dire che il telefono
     * sta mandando qualcosa che non è un'agenda — una ricorrenza espansa male, un
     * ciclo. La app li conosceva già, ma per copia: `MASSIMO_GIORNI_PER_CARICO` e
     * `MASSIMO_INTERVALLI_PER_CARICO`, sotto un commento che diceva «i tetti del
     * server, **ripetuti** qui». Ripetuti bene, per la ragione giusta — fermare
     * il carico prima di partire invece di prendere un 400 dopo — ma ripetuti.
     */
    carico: { giorni: { max: 120 }, intervalli: { max: 5_000 } },
};
/**
 * Il testo su cui si calcola il **riepilogo** delle impronte giornaliere.
 *
 * ## A che serve
 *
 * Nel caso normale il giro di sincronizzazione non trova niente di cambiato, e
 * le impronte che il telefono manda sono le stesse identiche del giro
 * precedente: con l'orizzonte predefinito sono 98, cioè 3.137 byte su 3.248 di
 * corpo — il 97% — rimandati ogni quarto d'ora perché nessuno chiedeva mai se
 * fossero cambiate. Il riepilogo è la stessa affermazione in sedici caratteri:
 * «i miei giorni sono ancora quelli che hai tu».
 *
 * ## Perché sta qui e non su una sponda sola
 *
 * Il telefono lo calcola sulle proprie impronte; il server lo **ricalcola**
 * sulle righe che ha in tabella, e i due devono coincidere carattere per
 * carattere o il `204` non arriva mai più — cioè si torna a mandare tutto,
 * sempre, in silenzio. È esattamente il modo di rompersi contro cui
 * `AGENDA_DISPOSITIVO.impronta.cifre` è stato dichiarato una volta sola, e per
 * la stessa ragione la forma canonica sta qui e non in due copie d'accordo per
 * gentilezza.
 *
 * Il server **ricalcola** invece di conservare: conservare vorrebbe dire una
 * colonna in più, e soprattutto un istante in cui quella colonna è vera e uno in
 * cui non lo è più — mentre ricalcolare risponde alla domanda di adesso. È anche
 * il motivo per cui questo non ha bisogno di nessuna migrazione.
 *
 * ## La forma
 *
 * `aaaa-mm-gg:impronta`, una riga per giornata, **in ordine di data**, separate
 * da un a capo. L'ordine è quello che serve: due telefoni con le stesse giornate
 * in ordine diverso descrivono la stessa agenda e devono dare lo stesso
 * riepilogo. La data entra nel testo e non solo l'impronta, perché un giorno
 * comparso e un giorno sparito con la stessa impronta sono due agende diverse.
 */
export function testoRiepilogoImpronte(impronte) {
    return Object.keys(impronte)
        .sort()
        .map((giorno) => `${giorno}:${impronte[giorno] ?? ''}`)
        .join('\n');
}
/**
 * «Quando sono libere queste persone»: le misure della domanda, non del carico.
 *
 * `persone.max` non nasce dal peso — il costo è di 36 byte al giorno a testa e
 * la fusione la fa il database — ma dal fatto che una riunione con più di
 * cinquanta invitati non è una riunione: è quasi sempre un elenco costruito per
 * sbaglio, e va rifiutato dicendo perché.
 *
 * `orizzonte.max` è il numero peggiore dei tre, perché non era scritto da
 * nessuna parte: il server lo calcolava a mano due volte come `MAX_QUERY_DAYS +
 * 30`, e la app ci arrivava con un `151` nudo dentro un ciclo — cioè la stessa
 * addizione fatta a mente, più un giorno di margine, in una forma in cui né il
 * centoventi né il trenta compaiono. Adesso l'addizione è una sola e il margine
 * è dichiarato.
 */
const DISPONIBILITA_GIORNI_MAX = 120;
/** Quanto un orizzonte dichiarato può spingersi oltre quello di una domanda. */
const DISPONIBILITA_ORIZZONTE_OLTRE = 30;
/**
 * Quante persone può tenere un **gruppo personale** — l'elenco che uno si fa per sé.
 *
 * È `DISPONIBILITA.persone.max - 1`, cioè il tetto dei convocati meno chi convoca, e non un
 * numero suo: un gruppo personale nasce per **convocare**, e un gruppo che non entra per intero
 * in una riunione è una trappola che si scopre al momento di usarlo, con l'elenco già scritto.
 *
 * ⚠ Il giorno che un gruppo servirà a una circolare invece che a una riunione — «tutta la
 * Produzione» sono 118 persone — questo numero va alzato **sapendo per cosa**, e allora non
 * potrà più derivare da quello dei convocati. La domanda sta in `spec/da-decidere.md` §28.
 */
export const GRUPPO_PERSONALE = {
    membri: { max: 49 },
    nome: { max: 40 },
};
/**
 * I luoghi personali di una riunione (coda §67, [[K667]]): «l'ufficio di Matteo», «il tavolo
 * davanti al mio ufficio all'HUB». Un nome e basta, ed è **per account** — Cosimo, 2026-10-19:
 * *«I LUOGHI PERSONALIZZATI SONO PER ACCOUNT»*. Non sono sale: nessuno li prenota e nessuno ne
 * controlla l'occupazione.
 */
export const LUOGO_PERSONALE = {
    nome: { max: 60 },
    /** Quanti se ne tiene una persona: un elenco da scegliere col pollice, non un archivio. */
    quanti: { max: 30 },
};
export const DISPONIBILITA = {
    persone: { max: 50 },
    giorni: { max: DISPONIBILITA_GIORNI_MAX },
    orizzonte: { max: DISPONIBILITA_GIORNI_MAX + DISPONIBILITA_ORIZZONTE_OLTRE },
};
/**
 * Il giro dell'autista: le note, le tappe, i vuoti, il contachilometri.
 *
 * Le tre note valgono mille l'una e sono **tre** dichiarazioni, non una: la
 * nota del giro la scrive chi pianifica, la nota della tappa la scrive
 * l'autista sul furgone, e `notaUfficio` è quella che l'ufficio lascia
 * all'autista su una fermata sola («il cancello è sul retro»). Tre autori, tre
 * momenti, tre ragioni per cambiare: uguale non vuol dire legato.
 *
 * `tappe.min` è due perché un giro ha una partenza e un arrivo — sotto non è un
 * giro. `tappe.max` è quaranta contro una produzione che faceva 5,4 fermate di
 * media e ne toccò 20 al massimo: è il doppio del peggior giro mai visto, non
 * una stima.
 *
 * `contachilometri.cifre` è l'unico numero di questo gruppo che il server **non**
 * ha: là c'è solo `@Min(0)`. Le nove cifre sono una comodità della tastiera —
 * `SchermataViaggio` le scriveva a mano due volte — e non producono nessun 400,
 * perché non c'è nulla al di là che le rifiuti. Sta scritto qui lo stesso, per
 * la ragione opposta a tutte le altre: perché chi un giorno mettesse un `@Max`
 * sul server trovi il numero che l'app usa già, invece di sceglierne un altro.
 */
export const VIAGGIO = {
    nota: { max: 1000 },
    notaUfficio: { max: 1000 },
    /**
     * La nota di una tappa.
     *
     * Il minimo è **uno** dal 2026-10-13, ed era tre. Finché su una consegna la nota era
     * obbligatoria, il tre non toglieva niente a nessuno: chi deve scrivere scrive una frase.
     * Tolto l'obbligo (ordine di Cosimo, il perché sta su `TripsService.completeStop`), quel
     * tre sarebbe rimasto a rifiutare «ok» — cioè l'unica cosa che qualcuno vuole scrivere su
     * una tappa senza storia. Una nota facoltativa si accetta, non si misura.
     */
    notaTappa: { min: 1, max: 1000 },
    tappe: { min: 2, max: 40 },
    /** La chiave dell'oggetto con la firma o la fotografia. Il file non entra mai nel database. */
    prova: { max: 300 },
    /** Europallet, ceste, subbi: quanti se ne prendono e se ne lasciano a una fermata. */
    vuoti: { min: 1, max: 9999 },
    contachilometri: { min: 0, cifre: 9 },
};
/**
 * Il residuo ferie come lo scrive l'ufficio: l'anno, i giorni, la nota.
 *
 * `anno` non è una precauzione generica: sotto il duemila e sopra il
 * duemilacento non è un anno di lavoro, è una data battuta storta — e senza il
 * muro una cifra di troppo scriveva un residuo nell'anno 20 260 che poi nessuno
 * ritrovava, perché nessuna schermata guarda là.
 *
 * `ore.max` è novemilanovecentonovantanove per entrambe le colonne
 * (`accruedHours` e `carriedHours`) e vale come muro dell'assurdo, non come
 * politica: un monte ore vero sta sotto il migliaio. Erano quattro numeri nudi
 * in una classe di otto righe.
 *
 * Si chiama `ore` e non `giorni` perché ore sono: le colonne, l'etichetta del
 * pannello («Ore maturate nell'anno») e il campo del DTO dicono tutti la stessa
 * unità. Il residuo si *mostra* in giorni — è la conversione di #63 — ma
 * mostrare in giorni e misurare in ore sono due cose diverse, e chiamare `giorni`
 * il tetto delle ore sarebbe stato il difetto di #129 introdotto da capo.
 */
export const RESIDUO = {
    anno: { min: 2000, max: 2100 },
    ore: { min: 0, max: 9999 },
    nota: { max: 500 },
};
/**
 * La timbratura: la nota e la pausa.
 *
 * `pausa.max` è otto ore in minuti. È il numero che il pannello conosceva già —
 * `PresenzeView` ha un `PAUSA_MASSIMA = 480` scritto a mano, con accanto un
 * commento che dice «il tetto del server» — e che il server aveva in tre punti
 * diversi (`clock.dto.ts` una volta, `correct-worktime.dto.ts` due). Quattro
 * copie dello stesso 480, e l'unica che dichiarava di esserlo era quella del
 * pannello.
 *
 * `nota.max` è cinquecento, e sta in tre classi: la timbratura che si crea, la
 * correzione e l'inserimento a mano.
 *
 * `rettifica.max` è diciotto ore, ed è il tetto dei minuti che una giornata contesa può
 * valere dopo la firma di una persona. Non è il tetto di una sessione — è più alto, perché
 * una giornata può averne due — ed è il vincolo `CHECK` che sta anche sulla tabella
 * `work_day_reviews`: la rettifica non deve poter diventare la porta di servizio da cui
 * scrivere una giornata di venti ore. Sta qui e non nella sola DTO perché il numero lo
 * conoscono in due — il server che rifiuta e il pannello che non fa nemmeno digitare — e
 * quello del pannello, scritto a mano, sarebbe la copia destinata a invecchiare (§60).
 */
export const TIMBRATURA = {
    nota: { max: 500 },
    pausa: { min: 0, max: 480 },
    rettifica: { min: 0, max: 18 * 60 },
};
/**
 * La ricerca dei colleghi nel selettore di persone.
 *
 * Non è `RICERCA_MAX`: quella è la ricerca degli elenchi paginati, che passa da
 * `PaginationQueryDto`. Questa è la sua sorella con un minimo — due caratteri,
 * perché sotto i due non è una ricerca, è tutta l'azienda — e con un `limit`
 * proprio, che decide quante facce tornano indietro.
 */
export const RICERCA_PERSONE = {
    testo: { min: 2, max: 100 },
    quante: { min: 1, max: 50 },
};
/**
 * Il nome di un'automazione, come filtro sullo storico delle esecuzioni.
 *
 * Cento caratteri non sono il nome di un'automazione: sono il muro oltre il
 * quale la stringa non è un filtro. Il filtro non è ristretto al catalogo di
 * proposito — `job_runs` conserva anche le automazioni ritirate, ed è il motivo
 * per cui la tabella esiste — e questa è la sola misura che gli resta.
 */
export const AUTOMAZIONE = {
    nome: { max: 100 },
};
/**
 * L'accesso: i due minimi sotto cui la richiesta non è un tentativo di entrare,
 * è un campo lasciato vuoto.
 *
 * Non è la politica delle password, e la differenza qui non è accademica: una
 * password **presentata** non si giudica, si confronta. Chi entra non la sta
 * scegliendo — ce l'ha già, e gliel'abbiamo data noi. Il minimo di una password
 * **scelta** è `security.minPasswordLength`, si legge dalla configurazione a ogni
 * cambio, e vale dove una password nasce: `ChangePasswordDto.newPassword` e il
 * ripristino. Non qui.
 *
 * ⚠ **Il sei è diventato uno il 2026-10-13, e non per simmetria.** Questo commento
 * diceva che il sei era «una domanda aperta» a cui avrebbe risposto chi decide la
 * politica. Ha risposto la produzione: su 416 persone attive, **33 avevano dal
 * gestionale una parola d'ordine più corta di sei caratteri** (3 da quattro, 30 da
 * cinque) e **tutte e 33 non erano mai riuscite a entrare**. Zero su trentatré. Il
 * muro davanti alla porta non teneva fuori chi indovina: teneva fuori chi sa.
 *
 * E rispondeva la cosa peggiore, cioè «Scrivi la password.» a chi l'aveva scritta.
 *
 * Il principio era **già scritto** in questo prodotto, su
 * `ChangePasswordDto.currentPassword`: «Qui la password non si sceglie: si dichiara
 * quella che si ha già», e lì il minimo è uno. Al login non era stato applicato.
 *
 * Quel che difende la porta dai tentativi è un'altra cosa e resta intera: il conto dei
 * fallimenti, il blocco a tempo (`locked_until`) e il limitatore. La lunghezza non ha
 * mai fermato nessuno che provasse — chi prova le password prova quelle lunghe.
 */
export const ACCESSO = {
    identificativo: { min: 2 },
    /**
     * Uno: la password presentata deve esserci. Non è un numero da alzare — alzarlo
     * chiude fuori chi ha una parola corta, che è precisamente quel che è successo.
     */
    password: { min: 1 },
};
/**
 * I turni di lavoro. I tre standard portano l'orario **qui**, non nello schema: il nome
 * dell'enum dice quale turno, questa costante a che ora comincia e finisce (in minuti dalla
 * mezzanotte). La notte scavalca la mezzanotte — la sua fine è il mattino dopo, e chi
 * disegna lo sa da `fine < inizio`. L'orario libero (`CUSTOM`) scrive i suoi minuti sulla
 * riga; questi sono la costante dei tre standard, uguali per tutta l'azienda.
 */
export const TURNO = {
    orari: {
        MORNING: { inizio: 6 * 60, fine: 14 * 60 },
        AFTERNOON: { inizio: 14 * 60, fine: 22 * 60 },
        NIGHT: { inizio: 22 * 60, fine: 6 * 60 },
    },
    /** Il giorno della settimana: 1 = lunedì … 6 = sabato. La domenica non ha turni. */
    giorno: { min: 1, max: 6 },
    /** L'orario libero, in minuti dalla mezzanotte. */
    minuti: { min: 0, max: 1439 },
    /** La nota su un turno (personale o di reparto). */
    nota: { max: 500 },
};
/**
 * Le costanti delle valutazioni delle prestazioni (D43), riprese dall'app Flask «Valutazione
 * Personale». `criteri` è l'**ordine di visualizzazione** — lo stesso con cui la scheda del
 * dipendente elenca i sei contributi — e non un ordine di importanza; le etichette sono la forma
 * leggibile dei valori dell'enum `Criterio`. `punti` è l'intervallo del peso di un evento:
 * l'app d'origine accetta da -4 a +4 e **non** lo 0 (lo 0 è «nessun evento», cioè nessuna riga).
 */
export const VALUTAZIONE = {
    criteri: [
        'QUALITA',
        'PRODUTTIVITA',
        'DISPONIBILITA',
        'POLIVALENZA',
        'COLLABORAZIONE',
        'AFFIDABILITA',
    ],
    etichette: {
        QUALITA: 'Qualità',
        PRODUTTIVITA: 'Produttività',
        DISPONIBILITA: 'Disponibilità',
        POLIVALENZA: 'Polivalenza',
        COLLABORAZIONE: 'Collaborazione',
        AFFIDABILITA: 'Affidabilità',
    },
    /** Il peso di un evento: da -4 a +4, mai 0. */
    punti: { min: -4, max: 4 },
    /** La nota concreta sull'episodio (facoltativa). */
    nota: { max: 1000 },
};
/**
 * Il contesto «turno» che una segnalazione porta dentro un messaggio di chat.
 *
 * Quando il dipendente segnala un'anomalia al responsabile, il turno di cui parla viaggia
 * **dentro** il messaggio come valore — non un id, perché «i miei turni» non ne espone uno:
 * la coordinata pubblica di una cella è `(reparto, lunedì, giorno)`, e per disegnarla bastano
 * il giorno, il tipo e — solo per il su misura — gli estremi. Il responsabile lo vede senza
 * doverlo chiedere. `CONTESTO.turno` è il discriminatore, gemello di `context_type` sullo
 * schema.
 */
export const CONTESTO = { turno: 'shift', riunione: 'meeting' };
/** Vero se `x` è un contesto-turno ben formato: la guardia che il client usa prima di disegnarlo. */
export function eContestoTurno(x) {
    if (typeof x !== 'object' || x === null)
        return false;
    const o = x;
    if (o.type !== CONTESTO.turno)
        return false;
    if (typeof o.weekStart !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(o.weekStart))
        return false;
    if (typeof o.weekday !== 'number' || o.weekday < TURNO.giorno.min || o.weekday > TURNO.giorno.max) {
        return false;
    }
    if (typeof o.kind !== 'string' || !Object.values(ShiftKind).includes(o.kind)) {
        return false;
    }
    const min = (v) => v === null || (typeof v === 'number' && Number.isInteger(v));
    return min(o.customStartMin) && min(o.customEndMin);
}
/** Vero se `x` è un contesto-riunione ben formato: la guardia che il client usa prima di disegnarlo. */
export function eContestoRiunione(x) {
    if (typeof x !== 'object' || x === null)
        return false;
    const o = x;
    if (o.type !== CONTESTO.riunione)
        return false;
    if (typeof o.eventId !== 'number' || !Number.isInteger(o.eventId) || o.eventId <= 0)
        return false;
    if (typeof o.titolo !== 'string' || o.titolo.length === 0)
        return false;
    if (typeof o.inizio !== 'string' || Number.isNaN(Date.parse(o.inizio)))
        return false;
    /*
      `risposta` si tollera assente e nulla, e si rifiuta solo se **c'e' e non e' uno stato noto**.
      Tre forme ammesse perche' tre esistono davvero: il messaggio scritto prima che la colonna
      nascesse (assente), chi scrive senza essere invitato (`null`), e la risposta vera. Pretenderla
      farebbe sparire dalle conversazioni ogni card gia' scritta.
    */
    if (o.risposta === undefined || o.risposta === null)
        return true;
    return (typeof o.risposta === 'string' &&
        Object.values(ParticipantStatus).includes(o.risposta));
}
/**
 * Il contesto **come si manda**, che non e' il contesto come si disegna.
 *
 * Toglie `risposta`, e lo fa per due ragioni che tirano nella stessa direzione:
 *
 *  1. **il server la timbra**, leggendola da `calendar_event_participants` al momento dell'invio.
 *     Quella del client sarebbe un'opinione su un fatto che il server ha gia' in mano;
 *  2. il `ValidationPipe` gira con `forbidNonWhitelisted: true`, quindi un campo che il DTO non
 *     dichiara **non e' ignorato: e' un 400**. Mandarla romperebbe l'invio, e lo romperebbe solo
 *     sulle riunioni allegate — cioe' nel caso meno percorso.
 *
 * Il client la porta lo stesso nel contesto «in sospeso» sopra il campo, perche' li' serve a
 * disegnare l'anteprima di quello che si sta allegando; qui, sul confine con la rete, esce.
 */
export function contestoDaInviare(contesto) {
    if (contesto.type !== CONTESTO.riunione)
        return contesto;
    const { risposta: _risposta, ...resto } = contesto;
    return resto;
}
/** Vero se `x` è uno dei contesti che il client sa disegnare. */
export function eContestoChat(x) {
    return eContestoTurno(x) || eContestoRiunione(x);
}
//# sourceMappingURL=api.js.map